// Pure panel-space layout for the graph-peek dot map: fixed ghost geography
// (five zones that fill the panel edge to edge) plus the zone-anchored
// projection that places each node's dot at its real relative layout position
// within its region. No React, no DOM — just coordinate maths.

import { type Region, nodeTypeToRegion, deriveRegionBoxes } from '@/lib/graph-regions';

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface Point {
  x: number;
  y: number;
}

const EDGE_INSET = 8;
const ZONE_GAP = 8;
// Bands take ~13% of panel height each; the spine zones share the rest.
const BAND_FRACTION = 0.13;
// Spine widths left to right. jtbd is widest: it holds four node types.
const SPINE_SPLIT: ReadonlyArray<{ region: Region; fraction: number }> = [
  { region: 'idea', fraction: 0.2 },
  { region: 'capabilities', fraction: 0.25 },
  { region: 'jtbd', fraction: 0.55 },
];
// Keeps dots off zone edges. Sized for the dots themselves, not the pulse
// ring's full 3× sweep — a clamped dot's fading ring may kiss the zone edge,
// a transient we accept rather than shrink narrow zones' usable field.
const ZONE_PADDING = 14;
// Max graph→panel scale. Sparse content stays a small honest cluster in the
// middle of its territory instead of stretching to fake density.
const MAX_SCALE = 0.5;
// Node centre offset from the stored top-left position — half the default
// footprint deriveRegionBoxes assumes (250×80).
const NODE_CENTRE_X = 125;
const NODE_CENTRE_Y = 40;

export function deriveZoneRects(width: number, height: number): Record<Region, Rect> {
  const innerW = width - 2 * EDGE_INSET;
  const bandH = Math.round(height * BAND_FRACTION);
  const middleY = EDGE_INSET + bandH + ZONE_GAP;
  const middleH = height - 2 * EDGE_INSET - 2 * bandH - 2 * ZONE_GAP;
  const spineW = innerW - ZONE_GAP * (SPINE_SPLIT.length - 1);
  const spine: Partial<Record<Region, Rect>> = {};
  let x = EDGE_INSET;
  for (const { region, fraction } of SPINE_SPLIT) {
    const w = Math.round(spineW * fraction);
    spine[region] = { x, y: middleY, width: w, height: middleH };
    x += w + ZONE_GAP;
  }
  return {
    signals: { x: EDGE_INSET, y: EDGE_INSET, width: innerW, height: bandH },
    idea: spine.idea!,
    capabilities: spine.capabilities!,
    jtbd: spine.jtbd!,
    assumptions: { x: EDGE_INSET, y: height - EDGE_INSET - bandH, width: innerW, height: bandH },
  };
}

// The evidence bands wrap-fill instead of using the box projection: elk lays
// the overlay in one long horizontal strip, so an aspect-preserving fit of a
// wide-short region collapses every dot onto a ruler-straight line. The
// overlay's x-spread is an attachment artefact, not meaningful geography —
// a row-major wrap (ordered by layout x, alternate rows staggered) keeps the
// density readable and fills the band's height.
const BAND_REGIONS = new Set<Region>(['signals', 'assumptions']);
const BAND_PITCH = 14;

function wrapBandDots(ids: string[], inner: Rect, out: Map<string, Point>): void {
  const cols = Math.max(1, Math.floor(inner.width / BAND_PITCH) + 1);
  const rows = Math.ceil(ids.length / cols);
  const pitchY = rows > 1 ? Math.min(BAND_PITCH, inner.height / (rows - 1)) : 0;
  const y0 = inner.y + (inner.height - pitchY * (rows - 1)) / 2;
  ids.forEach((id, i) => {
    const row = Math.floor(i / cols);
    const col = i % cols;
    const inRow = row === rows - 1 ? ids.length - row * cols : cols;
    const stagger = row % 2 === 1 ? BAND_PITCH / 2 : 0;
    const rowWidth = (inRow - 1) * BAND_PITCH + stagger;
    const x0 = inner.x + Math.max(0, (inner.width - rowWidth) / 2);
    out.set(id, {
      x: Math.min(inner.x + inner.width, x0 + stagger + col * BAND_PITCH),
      y: y0 + row * pitchY,
    });
  });
}

export function projectDots(
  nodes: Array<{ id: string; type: string }>,
  positions: Map<string, Point>,
  zones: Record<Region, Rect>,
): Map<string, Point> {
  // Only nodes the layout has placed participate — a half-loaded position map
  // must not scatter dots at (0,0).
  const placed = nodes.filter((n) => positions.has(n.id));
  const boxes = new Map(
    deriveRegionBoxes(placed.map((n) => ({ type: n.type, position: positions.get(n.id)! }))).map(
      (b) => [b.region, b.rect],
    ),
  );
  const innerFor = (region: Region): Rect => {
    const zone = zones[region];
    return {
      x: zone.x + ZONE_PADDING,
      y: zone.y + ZONE_PADDING,
      width: Math.max(1, zone.width - 2 * ZONE_PADDING),
      height: Math.max(1, zone.height - 2 * ZONE_PADDING),
    };
  };
  const out = new Map<string, Point>();
  const bandNodes = new Map<Region, Array<{ id: string; x: number }>>();
  for (const node of placed) {
    const region = nodeTypeToRegion(node.type);
    if (!region) continue;
    if (BAND_REGIONS.has(region)) {
      const bucket = bandNodes.get(region) ?? [];
      bucket.push({ id: node.id, x: positions.get(node.id)!.x });
      bandNodes.set(region, bucket);
      continue;
    }
    const box = boxes.get(region);
    if (!box) continue;
    const inner = innerFor(region);
    const scale = Math.min(MAX_SCALE, inner.width / box.width, inner.height / box.height);
    const pos = positions.get(node.id)!;
    const x = inner.x + inner.width / 2 + (pos.x + NODE_CENTRE_X - (box.x + box.width / 2)) * scale;
    const y = inner.y + inner.height / 2 + (pos.y + NODE_CENTRE_Y - (box.y + box.height / 2)) * scale;
    out.set(node.id, {
      x: Math.min(inner.x + inner.width, Math.max(inner.x, x)),
      y: Math.min(inner.y + inner.height, Math.max(inner.y, y)),
    });
  }
  for (const [region, bucket] of bandNodes) {
    bucket.sort((a, b) => a.x - b.x || a.id.localeCompare(b.id));
    wrapBandDots(
      bucket.map((n) => n.id),
      innerFor(region),
      out,
    );
  }
  return out;
}
